import _ from 'lodash'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { LoadingButton } from '@mui/lab'
import { Box, Divider, Grid, Paper, Stack, Typography } from '@mui/material'

import AutocompleteInput, { AutocompleteOption } from '@/components/ui/inputs/AutocompleteInput'

import ErrorMessage from '@/components/formik/messages/ErrorMessage'
import TextInputController from '@/components/ui/inputs/TextInputController'

import AssignedLicensesTable from './AssignedLicensesTable'

const contractFormSchema = yup.object({
  establishment: yup.string().required(),
  licenseUsages: yup
    .array(
      yup
        .object({
          license: yup
            .object({
              id: yup.string().required(),
              name: yup.string().required(),
            })
            .required(),
          quantity: yup
            .number()
            .min(1)
            .required()
            .test('isGreaterOrEqualThan', 'error.license.lackOfQuantity.message', (value, testContext) => {
              const { licensees } = testContext.parent
              return value >= (licensees?.length ?? 0)
            }),
          licensees: yup.array(yup.string().required()),
        })
        .required()
    )
    .required(),
  name: yup.string().required(),
})

export type ContractFormValues = yup.InferType<typeof contractFormSchema>

export type ContractFormProps = {
  establishmentOptions: AutocompleteOption[]
  initialValues?: ContractFormValues
  isSubmitting?: boolean
  licenseOptions: AutocompleteOption[]
  onSubmit: (values: ContractFormValues) => void
  readOnly?: boolean
  staffOptions?: AutocompleteOption[]
}

export function ContractForm(props: Readonly<ContractFormProps>) {
  const { establishmentOptions, initialValues, isSubmitting = false, licenseOptions, onSubmit: onSubmitProp, readOnly = false, staffOptions } = props
  const { t } = useTranslation()

  const methods = useForm<ContractFormValues>({
    defaultValues: initialValues ?? { establishment: '', licenseUsages: [], name: '' },
    resolver: yupResolver(contractFormSchema),
    mode: readOnly ? 'onSubmit' : 'onBlur',
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = methods

  const licenseUsageFieldArray = useFieldArray({ control, name: 'licenseUsages' })

  useEffect(() => {
    reset(initialValues)
  }, [initialValues, reset])

  const onSubmit: SubmitHandler<ContractFormValues> = (values) => {
    onSubmitProp(values)
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={4}>
          <Paper>
            <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
                  {t('form.contract.name.title')}
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <Grid container rowGap={3} sx={{ p: 3 }}>
              <Grid item xs={12} md={6}>
                <TextInputController control={control} fullWidth label={t('field.name.label')} name="name" readOnly={readOnly} />
              </Grid>
            </Grid>
          </Paper>
          <Paper>
            <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
                  {t('form.contract.establishment.title')}
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <Grid container rowGap={3} sx={{ p: 3 }}>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="establishment"
                  render={({ field }) => (
                    <AutocompleteInput
                      error={!!errors.establishment}
                      freeSolo={false}
                      helperText={!!errors.establishment && <ErrorMessage error={errors.establishment.message} />}
                      label={t('field.establishment.label')}
                      name={field.name}
                      onChange={(selectedOption) => {
                        setValue('establishment', Array.isArray(selectedOption) ? _.map(selectedOption, 'value')[0] : selectedOption?.value || '', {
                          shouldValidate: true,
                        })
                      }}
                      options={establishmentOptions}
                      readOnly={readOnly}
                      value={establishmentOptions.find((option) => option.value === field.value) ?? null}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>
          <Paper>
            <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
                  {t('form.contract.licenses.title')}
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <Grid container rowGap={3} sx={{ p: 3 }}>
              <Grid item xs={12}>
                {!readOnly && (
                  <Controller
                    control={control}
                    name="licenseUsages"
                    render={({ field }) => (
                      <AutocompleteInput
                        label={t('field.licenses.label')}
                        multiple
                        onChange={(selectedOptions) => {
                          if (Array.isArray(selectedOptions)) {
                            const addedOptions = selectedOptions.filter((option) => !field.value.find(({ license }) => license.id === option.value))
                            const removedLicenseIds = field.value
                              .filter(({ license }) => !selectedOptions.find((option) => license.id === option.value))
                              .map(({ license }) => license.id)

                            if (removedLicenseIds.length) {
                              licenseUsageFieldArray.remove(
                                removedLicenseIds.map((licenseId) => field.value.findIndex(({ license }) => license.id === licenseId))
                              )
                            }
                            if (addedOptions.length) {
                              licenseUsageFieldArray.append(addedOptions.map((option) => ({ license: { id: option.value, name: option.label }, quantity: 0 })))
                            }
                          }
                        }}
                        options={licenseOptions}
                        readOnly={readOnly}
                        value={licenseOptions.filter((option) => field.value.find(({ license }) => license.id === option.value))}
                      />
                    )}
                  />
                )}
                <Box mt={readOnly ? 0 : 2}>
                  <AssignedLicensesTable
                    disableAssign={!initialValues}
                    onDelete={(id) => {
                      const index = licenseUsageFieldArray.fields.findIndex((field) => field.license.id === id)
                      licenseUsageFieldArray.remove(index)
                    }}
                    readOnly={readOnly}
                    staffOptions={staffOptions}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
          <Stack direction="row" justifyContent="end">
            {!readOnly && (
              <LoadingButton loading={isSubmitting} type="submit" variant="contained">
                {t('common:button.submit.label')}
              </LoadingButton>
            )}
          </Stack>
        </Stack>
      </form>
    </FormProvider>
  )
}

export default ContractForm
